import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// GA4 properties for the admin app. The selection is hostname-based (rather than
// build-time environment config) because BOTH the dev and prod admin deploys
// build with the production Angular configuration — only the served hostname
// distinguishes them. The production admin app is served exclusively from
// admin.kernalhq.app; every other host (the dev site admin.dev.kernalhq.app,
// CloudFront preview URLs, and local dev on localhost / 127.0.0.1) uses the
// non-production property, so dev/local traffic never reaches production GA.
const GA_PROD_ID    = 'G-LJGRK6MFBK';
const GA_NONPROD_ID = 'G-2VMKDD15W2';
const GA_PROD_HOSTS: readonly string[] = ['admin.kernalhq.app'];

/** Single source of truth for prod-vs-non-prod GA4 property selection. */
export function resolveGaMeasurementId(hostname: string): string {
  return GA_PROD_HOSTS.includes(hostname) ? GA_PROD_ID : GA_NONPROD_ID;
}

/**
 * Google Analytics 4 (gtag.js) for the admin SPA.
 *
 * Angular's client-side router does not trigger gtag's automatic page views, so
 * we configure GA with `send_page_view: false` and emit exactly one `page_view`
 * per completed navigation (`NavigationEnd`) — including the initial load. This
 * gives correct SPA tracking with no double-counting and never fires on
 * partial/incomplete navigations (only NavigationEnd is tracked).
 *
 * The measurement ID is selected by hostname (see `resolveGaMeasurementId`):
 * production property on admin.kernalhq.app, non-production property everywhere
 * else (dev site, previews, localhost). Exactly one tag loads per page load.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private started = false;

  constructor(private readonly router: Router) {}

  /** Loads gtag.js once and begins SPA route page-view tracking. Safe to call once at app startup. */
  init(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;

    const id = resolveGaMeasurementId(window.location.hostname);

    // 1. Inject the gtag.js loader exactly once (single selected property).
    const loader = document.createElement('script');
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(loader);

    // 2. Bootstrap the gtag shim.
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]): void { window.dataLayer.push(args); };
    window.gtag('js', new Date());
    // send_page_view:false → page views are emitted manually on each NavigationEnd below.
    window.gtag('config', id, { send_page_view: false, anonymize_ip: true });

    // 3. One page_view per completed navigation (the first NavigationEnd covers the initial load).
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.trackPageView(e.urlAfterRedirects));
  }

  private trackPageView(path: string): void {
    if (!window.gtag) return; // guard against early render / load-timing
    window.gtag('event', 'page_view', {
      page_location: window.location.href,
      page_path: path,
      page_title: document.title,
    });
  }
}
